/**
 * Unified CDR report (brief §5 Phase 2).
 *
 * Filters + pagination over `GET /api/cdr`; CSV export (gated by
 * `cdr.export`) reuses the same filters. Rows link to the call detail with
 * legs, the stage timeline, and recording playback.
 */

import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { cdrExportUrl, getCdrs } from "@pbxmgr/api";
import type { CdrFilters, CdrRow } from "@pbxmgr/api";
import { Alert, Badge, Button, Card, Spinner, TextField } from "@pbxmgr/ui";

import { usePermission } from "../auth/usePermission";

const PAGE_SIZE = 50;

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

/** Convert a `datetime-local` input value to the API's ISO format. */
function toIso(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function CdrTable({ rows }: { rows: CdrRow[] }): ReactNode {
  if (rows.length === 0) return <div className="ui-empty">No calls match the current filters.</div>;
  return (
    <table className="ui-table">
      <thead>
        <tr>
          <th>Start</th>
          <th>Direction</th>
          <th>From</th>
          <th>To</th>
          <th>Extension</th>
          <th>Trunk</th>
          <th>Duration</th>
          <th>Disposition</th>
          <th>Node</th>
          <th>Rec</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <td>
              <Link to={`/cdr/${r.id}`}>{formatWhen(r.start_time)}</Link>
            </td>
            <td>{r.direction ?? "—"}</td>
            <td>{r.from_uri ?? "—"}</td>
            <td>{r.to_uri ?? "—"}</td>
            <td>{r.extension ?? "—"}</td>
            <td>{r.trunk ?? "—"}</td>
            <td>{formatDuration(r.duration_seconds)}</td>
            <td>{r.disposition ?? "—"}</td>
            <td>{r.source_node_id}</td>
            <td>{r.has_recording ? <Badge tone="online">audio</Badge> : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function CdrPage(): ReactNode {
  // Draft filter inputs; applied to the query only on submit.
  const [search, setSearch] = useState("");
  const [extension, setExtension] = useState("");
  const [direction, setDirection] = useState("");
  const [after, setAfter] = useState("");
  const [before, setBefore] = useState("");

  const [filters, setFilters] = useState<CdrFilters>({});
  const [offset, setOffset] = useState(0);

  const canExport = usePermission("cdr.export");

  const page = useQuery({
    queryKey: ["cdr", filters, offset],
    queryFn: () => getCdrs(filters, PAGE_SIZE, offset),
    placeholderData: keepPreviousData,
  });

  const apply = (event: FormEvent): void => {
    event.preventDefault();
    setOffset(0);
    setFilters({
      search: search || undefined,
      extension: extension || undefined,
      direction: direction || undefined,
      start_after: toIso(after),
      start_before: toIso(before),
    });
  };

  const total = page.data?.total ?? 0;
  const rows = page.data?.items ?? [];
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = offset + rows.length;

  return (
    <div>
      <div className="dash__head">
        <h1>Reporting</h1>
        {canExport ? (
          <a href={cdrExportUrl(filters)} download>
            Export CSV
          </a>
        ) : null}
      </div>

      <Card>
        <form className="cdr-filters" onSubmit={apply}>
          <TextField
            id="cdr-search"
            label="Search (from/to/call id)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <TextField
            id="cdr-ext"
            label="Extension"
            value={extension}
            onChange={(e) => setExtension(e.target.value)}
          />
          <div className="ui-field">
            <label htmlFor="cdr-dir">Direction</label>
            <select id="cdr-dir" value={direction} onChange={(e) => setDirection(e.target.value)}>
              <option value="">any</option>
              <option value="inbound">inbound</option>
              <option value="outbound">outbound</option>
              <option value="internal">internal</option>
            </select>
          </div>
          <TextField
            id="cdr-after"
            label="From"
            type="datetime-local"
            value={after}
            onChange={(e) => setAfter(e.target.value)}
          />
          <TextField
            id="cdr-before"
            label="To"
            type="datetime-local"
            value={before}
            onChange={(e) => setBefore(e.target.value)}
          />
          <Button type="submit">Apply</Button>
        </form>
      </Card>

      <section className="dash-panel">
        <h2>
          Calls {pageStart}–{pageEnd} of {total}
        </h2>
        <Card>
          {page.isLoading ? (
            <Spinner />
          ) : page.isError ? (
            <Alert>Failed to load the report.</Alert>
          ) : (
            <CdrTable rows={rows} />
          )}
          <div className="cdr-pager">
            <Button
              variant="secondary"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              disabled={pageEnd >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </Card>
      </section>
    </div>
  );
}
